import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, Package, Truck, PackageCheck, PackageX, ArrowRight, Clock, MapPin, ExternalLink, ShieldCheck } from "lucide-react";
import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  received:         { label: "Received at Mailroom",   icon: PackageCheck, tone: "bg-red-50 text-primary border-primary/30" },
  in_transit:       { label: "In Transit",              icon: Truck,        tone: "bg-red-50 text-primary border-primary/30" },
  out_for_delivery: { label: "Out for Delivery",        icon: Truck,        tone: "bg-red-50 text-primary border-primary/30" },
  forwarded:        { label: "Forwarded",               icon: ArrowRight,   tone: "bg-red-50 text-primary border-primary/30" },
  delivered:        { label: "Delivered",               icon: PackageCheck, tone: "bg-emerald-50 text-emerald-700 border-emerald-300" },
  returned:         { label: "Returned",                icon: PackageX,     tone: "bg-amber-50 text-amber-800 border-amber-300" },
};

const prettyDate = (s) => {
  if (!s) return "—";
  try { return new Date(s).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); } catch { return s; }
};

export default function PublicTrackingPage() {
  const { tracking_number } = useParams();
  const nav = useNavigate();
  const [query, setQuery] = useState(tracking_number || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchTrack = async (tn) => {
    if (!tn) return;
    setLoading(true); setError(""); setData(null);
    try {
      const { data } = await axios.get(`${BACKEND}/api/public/track/${encodeURIComponent(tn.trim())}`);
      setData(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Unable to fetch tracking details.");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (tracking_number) fetchTrack(tracking_number);
  }, [tracking_number]);

  const onSubmit = (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    nav(`/track/${encodeURIComponent(query.trim().toUpperCase())}`);
  };

  const statusMeta = data ? (STATUS_META[data.status] || { label: data.status, icon: Package, tone: "bg-muted text-foreground border-border" }) : null;
  const StatusIcon = statusMeta?.icon || Package;

  return (
    <div className="min-h-screen bg-white text-foreground flex flex-col" data-testid="public-track-page">
      {/* Red hero header */}
      <header className="bg-primary text-primary-foreground">
        <div className="max-w-5xl mx-auto px-6 py-8">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white text-primary flex items-center justify-center font-display font-bold rounded-md">M</div>
            <div>
              <div className="font-display text-xl leading-none">Mailroom<span className="text-white/80">.OS</span></div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-white/70 mt-1 font-semibold">Parcel Tracking Portal</div>
            </div>
          </div>
          <div className="mt-6 max-w-2xl">
            <h1 className="text-3xl sm:text-4xl font-display font-bold leading-tight">
              Track your parcel<br /><span className="text-white/85">in real time.</span>
            </h1>
            <p className="text-white/80 text-sm mt-2 max-w-md">Enter your tracking number to view real-time status, custody events and delivery updates.</p>
          </div>
        </div>
      </header>

      {/* Search bar (elevated over red header) */}
      <div className="max-w-5xl w-full mx-auto px-6 -mt-6 z-10">
        <form onSubmit={onSubmit} className="bg-white rounded-lg shadow-lg border border-border p-3 flex flex-col sm:flex-row gap-3">
          <div className="flex-1 relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid="public-track-input"
              value={query}
              onChange={(e) => setQuery(e.target.value.toUpperCase())}
              placeholder="Enter tracking number (e.g. MR-A1B2C3D4E5)"
              className="pl-9 h-11 font-mono uppercase text-sm border-input focus-visible:ring-primary"
            />
          </div>
          <Button
            type="submit"
            data-testid="public-track-submit"
            className="h-11 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 font-semibold px-6"
            disabled={loading}
          >
            {loading ? "Searching…" : "Track parcel →"}
          </Button>
        </form>
      </div>

      {/* Result panel */}
      <div className="max-w-5xl w-full mx-auto px-6 py-10 flex-1">
        {error && !loading && (
          <div className="bg-red-50 border border-primary/30 text-primary rounded-lg p-6 text-center reveal" data-testid="public-track-error">
            <PackageX className="mx-auto mb-2" size={28} />
            <div className="font-semibold">{error}</div>
            <div className="text-xs text-muted-foreground mt-1">Double-check the tracking number or contact your mailroom for support.</div>
          </div>
        )}

        {!error && !data && !loading && (
          <div className="border border-dashed border-border rounded-lg p-10 text-center">
            <Package className="mx-auto mb-3 text-muted-foreground" size={32} />
            <div className="text-sm text-muted-foreground">Enter a tracking number above to see live status.</div>
          </div>
        )}

        {data && (
          <div className="grid lg:grid-cols-3 gap-6 reveal" data-testid="public-track-result">
            {/* Left: Status card */}
            <div className="lg:col-span-2 space-y-6">
              <div className="border border-border rounded-lg overflow-hidden">
                <div className="bg-primary text-primary-foreground px-6 py-5 flex items-center justify-between">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-white/70 font-semibold">Reference ID</div>
                    <div className="font-mono text-xl mt-1" data-testid="public-track-number">{data.tracking_number}</div>
                    {data.awb_number && (
                      <div className="mt-1.5">
                        <div className="text-[9px] uppercase tracking-[0.18em] text-white/60 font-semibold">AWB / Consignment</div>
                        <div className="font-mono text-sm mt-0.5" data-testid="public-track-awb">{data.awb_number}</div>
                      </div>
                    )}
                  </div>
                  <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-semibold bg-white text-primary`} data-testid="public-track-status">
                    <StatusIcon size={14} />
                    {statusMeta.label}
                  </div>
                </div>

                <div className="p-6 grid sm:grid-cols-2 gap-4 text-sm">
                  <Field label="Courier" value={data.courier || "—"} />
                  <Field label="Shipping Mode" value={data.shipping_mode || "—"} />
                  <Field label="Parcel Type" value={data.parcel_type || "—"} />
                  <Field label="Items" value={String(data.item_count || 1)} />
                  <Field label="From" value={data.sender_name || "—"} />
                  <Field label="To" value={data.recipient_name || "—"} />
                  {data.destination_city && <Field label="Destination" value={`${data.destination_city}${data.destination_pincode ? " · " + data.destination_pincode : ""}`} />}
                  {data.weight_kg ? <Field label="Weight" value={`${data.weight_kg} kg`} /> : null}
                </div>

                {data.external_tracking_url && (
                  <div className="px-6 pb-5">
                    <a
                      href={data.external_tracking_url}
                      target="_blank" rel="noreferrer"
                      data-testid="public-track-external"
                      className="inline-flex items-center gap-2 text-xs font-semibold text-primary hover:underline"
                    >
                      Track on {data.courier} <ExternalLink size={12} />
                    </a>
                  </div>
                )}
              </div>

              {/* Timeline */}
              <div className="border border-border rounded-lg p-6">
                <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground mb-4">Chain of custody</div>
                {(!data.timeline || data.timeline.length === 0) ? (
                  <div className="text-sm text-muted-foreground">No events yet.</div>
                ) : (
                  <ol className="space-y-4">
                    {[...data.timeline].reverse().map((ev, i) => {
                      const meta = STATUS_META[ev.status] || { label: ev.status, icon: Package };
                      const Icon = meta.icon;
                      const isLatest = i === 0;
                      return (
                        <li key={i} className="flex gap-3" data-testid={`public-track-event-${i}`}>
                          <div className={`w-8 h-8 shrink-0 rounded-full flex items-center justify-center ${isLatest ? "bg-primary text-white" : "bg-red-50 text-primary"}`}>
                            <Icon size={14} />
                          </div>
                          <div className="flex-1 pb-1">
                            <div className={`text-sm font-semibold ${isLatest ? "text-primary" : "text-foreground"}`}>{meta.label}</div>
                            <div className="text-[11px] text-muted-foreground flex items-center gap-1 mt-0.5">
                              <Clock size={11} /> {prettyDate(ev.at)}
                              {ev.by && <span>· by {ev.by}</span>}
                            </div>
                            {ev.remarks && <div className="text-xs text-muted-foreground mt-1 italic">"{ev.remarks}"</div>}
                          </div>
                        </li>
                      );
                    })}
                  </ol>
                )}
              </div>
            </div>

            {/* Right: Meta / brand */}
            <aside className="space-y-6">
              <div className="border border-border rounded-lg p-6 bg-red-50/50">
                <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground">Handled by</div>
                <div className="mt-2 text-lg font-display font-bold text-foreground">{data.tenant?.name || "Mailroom Operations"}</div>
                {data.tenant?.code && <div className="text-[11px] mono text-muted-foreground mt-1 uppercase tracking-widest">{data.tenant.code}</div>}
                <div className="mt-4 flex items-start gap-2 text-xs text-muted-foreground">
                  <ShieldCheck size={14} className="text-primary shrink-0 mt-0.5" />
                  <span>Auditable chain of custody · Signed digital handoffs · Only limited data is shown publicly.</span>
                </div>
              </div>
              <div className="border border-border rounded-lg p-6">
                <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground">Timing</div>
                <div className="mt-3 space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-muted-foreground">Created</span><span>{prettyDate(data.created_at)}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Last update</span><span>{prettyDate(data.updated_at)}</span></div>
                </div>
              </div>
            </aside>
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="border-t border-border py-6">
        <div className="max-w-5xl mx-auto px-6 flex items-center justify-between text-xs text-muted-foreground">
          <div>© Mailroom.OS · Enterprise mailroom platform</div>
          <a href="/login" className="text-primary font-semibold hover:underline" data-testid="public-track-staff-login">Staff sign in →</a>
        </div>
      </footer>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.14em] font-semibold text-muted-foreground">{label}</div>
      <div className="text-sm font-medium text-foreground mt-1 break-words">{value}</div>
    </div>
  );
}
